import { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'react-hot-toast';
import { Upload, X, Sparkles, Trophy, ChevronDown, Download, Share2, Loader2 } from 'lucide-react';
import ComprehensiveAnalysisResults from '../components/ComprehensiveAnalysisResults';
import AIEnhancement from '../components/AIEnhancement';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function BestOfThree({ darkMode }) {
  const { user, isAuthenticated, authToken } = useAuth();
  const navigate = useNavigate();
  
  // Upload state
  const [images, setImages] = useState([]);
  const [purpose, setPurpose] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 0, percentage: 0 });
  
  // Results state
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('winner');
  
  // Refs
  const fileInputRef = useRef(null);

  // Handle image upload
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    if (images.length + files.length > 3) {
      toast.error('Maximum 3 images allowed');
      return;
    }

    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substr(2, 9)
    }));

    setImages(prev => [...prev, ...newImages]);
  };

  // Remove image
  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  // Handle analysis
  const handleAnalyze = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to use this feature');
      return;
    }

    if (images.length < 2) {
      toast.error('Please upload at least 2 images');
      return;
    }

    if (!purpose.trim()) {
      toast.error('Please describe the purpose for comparison');
      return;
    }

    // Check credits
    if (user.credits < 15) {
      toast.error('Insufficient credits. You need 15 credits for this analysis.');
      return;
    }

    setLoading(true);
    setAnalysisProgress({ current: 0, total: images.length, percentage: 0 });

    try {
      const allResults = [];
      
      // Analyze each image
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        
        setAnalysisProgress({
          current: i + 1,
          total: images.length,
          percentage: Math.round(((i + 1) / images.length) * 100)
        });

        toast.info(`Analyzing image ${i + 1} of ${images.length}...`, { duration: 2000 });

        const formData = new FormData();
        formData.append('file', img.file);
        formData.append('user_id', user.id);
        formData.append('audience_filters', JSON.stringify({}));
        formData.append('custom_question', `Purpose: ${purpose}. Analyze this image and explain how well it serves this purpose.`);

        const response = await axios.post(`${API}/api/analyze/image`, formData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
          },
          timeout: 180000
        });

        allResults.push({
          ...response.data,
          imageIndex: i,
          preview: img.preview
        });
      }

      // Calculate winner based on overall scores
      const scoredResults = allResults.map(result => ({
        ...result,
        overallScore: calculateOverallScore(result)
      }));

      scoredResults.sort((a, b) => b.overallScore - a.overallScore);

      setResults(scoredResults);
      setActiveTab('winner');
      toast.success('Analysis complete!');

    } catch (error) {
      console.error('Analysis error:', error);
      toast.error(error.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate overall score
  const calculateOverallScore = (result) => {
    const valence = (result.valence || 0) * 100;
    const arousal = (result.arousal_level || 0) * 100;
    const dominance = (result.dominance || 0) * 100;
    return (valence + arousal + dominance) / 3;
  };

  // Reset
  const handleReset = () => {
    setImages([]);
    setPurpose('');
    setResults(null);
    setActiveTab('winner');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'}`}>
      {/* Header */}
      <div className={`border-b ${darkMode ? 'border-gray-700 bg-gray-800/80' : 'border-blue-100 bg-white/80'} backdrop-blur-sm sticky top-0 z-50`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8 text-yellow-500" />
            <div>
              <h1 className="text-2xl font-bold">Best of 3</h1>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Find the best image for your purpose
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {!results ? (
          /* Upload Interface */
          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}>
            <CardHeader>
              <CardTitle className={darkMode ? 'text-white' : 'text-gray-900'}>
                Upload Images to Compare (2-3 images)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Image Upload */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Select Images
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {images.map(img => (
                    <div key={img.id} className="relative">
                      <img
                        src={img.preview}
                        alt="Upload"
                        className="w-full h-48 object-cover rounded-lg border-2 border-blue-500"
                      />
                      <button
                        onClick={() => removeImage(img.id)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  
                  {images.length < 3 && (
                    <label className={`flex flex-col items-center justify-center h-48 border-2 border-dashed rounded-lg cursor-pointer ${darkMode ? 'border-gray-600 hover:border-blue-500' : 'border-gray-300 hover:border-blue-500'}`}>
                      <Upload className="w-8 h-8 mb-2 text-gray-400" />
                      <span className="text-sm text-gray-500">Click to upload</span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Purpose Input */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  What is the purpose? *
                </label>
                <Textarea
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder="e.g., Social media post to increase engagement, Product photo for e-commerce, Marketing campaign for millennials..."
                  className={`min-h-24 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                />
              </div>

              {/* Credits Info */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Credits Required:</span>
                  <span className="text-xl font-bold text-blue-600">15 credits</span>
                </div>
                {user && (
                  <div className="text-sm mt-1 text-gray-500">
                    Your balance: {user.credits} credits
                  </div>
                )}
              </div>

              {/* Analyze Button */}
              <Button
                onClick={handleAnalyze}
                disabled={loading || images.length < 2 || !purpose.trim()}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-6 text-lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing {analysisProgress.current} of {analysisProgress.total}...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Find Best Image
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Results Display */
          <div className="space-y-6">
            {/* Winner Card */}
            <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-2 border-yellow-400">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <Trophy className="w-12 h-12 text-yellow-600 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      🏆 Winner: Image #{results[0].imageIndex + 1}
                    </h3>
                    <p className={`mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Overall Score: <span className="font-bold text-xl text-green-600">{results[0].overallScore.toFixed(1)}/100</span>
                    </p>
                    <Button onClick={handleReset} variant="outline" className="mt-2">
                      Try New Comparison
                    </Button>
                  </div>
                  <img
                    src={results[0].preview}
                    alt="Winner"
                    className="w-32 h-32 object-cover rounded-lg border-4 border-yellow-400"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              <Button
                onClick={() => setActiveTab('winner')}
                variant={activeTab === 'winner' ? 'default' : 'outline'}
              >
                Winner Analysis
              </Button>
              {results.map((result, index) => (
                <Button
                  key={result.imageIndex}
                  onClick={() => setActiveTab(`image-${index}`)}
                  variant={activeTab === `image-${index}` ? 'default' : 'outline'}
                >
                  Image #{result.imageIndex + 1}
                </Button>
              ))}
              <Button
                onClick={() => setActiveTab('comparison')}
                variant={activeTab === 'comparison' ? 'default' : 'outline'}
              >
                Comparison Table
              </Button>
            </div>

            {/* Tab Content */}
            {activeTab === 'winner' && (
              <ComprehensiveAnalysisResults
                result={results[0]}
                darkMode={darkMode}
              />
            )}

            {activeTab.startsWith('image-') && (
              <ComprehensiveAnalysisResults
                result={results[parseInt(activeTab.split('-')[1])]}
                darkMode={darkMode}
              />
            )}

            {activeTab === 'comparison' && (
              <Card className={darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}>
                <CardHeader>
                  <CardTitle>Comparison Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={darkMode ? 'border-b border-gray-700' : 'border-b'}>
                          <th className="text-left p-3">Rank</th>
                          <th className="text-left p-3">Image</th>
                          <th className="text-left p-3">Overall Score</th>
                          <th className="text-left p-3">Valence</th>
                          <th className="text-left p-3">Arousal</th>
                          <th className="text-left p-3">Dominance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((result, index) => (
                          <tr key={result.imageIndex} className={darkMode ? 'border-b border-gray-700' : 'border-b'}>
                            <td className="p-3">#{index + 1}</td>
                            <td className="p-3">
                              <img src={result.preview} alt={`Image ${result.imageIndex + 1}`} className="w-16 h-16 object-cover rounded" />
                            </td>
                            <td className="p-3 font-bold text-lg">{result.overallScore.toFixed(1)}</td>
                            <td className="p-3">{((result.valence || 0) * 100).toFixed(0)}%</td>
                            <td className="p-3">{((result.arousal_level || 0) * 100).toFixed(0)}%</td>
                            <td className="p-3">{((result.dominance || 0) * 100).toFixed(0)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default BestOfThree;
